import { useState, useEffect } from "react";
import {
  getNewsFeed,
  getTenantById,
} from "../../../../../shared/services/apis";
import IframeViewer from "../../../../../shared/ui/urlOpener/IFrameViewer";
import saveAs from "file-saver";
import { RiDownloadLine } from "react-icons/ri";

interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}

interface NewsItem {
  title: string;
  url: string;
  domain: string;
  published_at: string;
  snippet: string;
  event_categories: string[];
}

interface NewsFeedProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}

export const NewsFeed = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
}: NewsFeedProps) => {
  const [selectedCategory, setSelectedCategory] = useState("All News");
  const [data, setData] = useState<NewsItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const [tenant, setTenant] = useState<Tenant | null>();

  useEffect(() => {
    if (!tenantId) return;

    const fetchTenant = async () => {
      try {
        const data = await getTenantById(tenantId);
        setTenant(data);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      }
    };

    fetchTenant();
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !tenantServiceId || !tenantServiceOptionId) return;

    const fetchData = async () => {
      try {
        const response = await getNewsFeed(
          tenantId,
          tenantServiceId,
          tenantServiceOptionId
        );
        setData(response);
      } catch (err) {
        console.error("Failed to fetch news feed:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tenantId, tenantServiceId, tenantServiceOptionId, loading]);

  // Download functions
const downloadPDF = async () => {
  setDownloadingPdf(true);
  try {
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const marginX = 20;
    const marginY = 25;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentWidth = pageWidth - marginX * 2;

    // Header
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("News Feed", marginX, marginY);

    // Line under header
    doc.setDrawColor(190);
    doc.setLineWidth(0.5);
    doc.line(marginX, marginY + 3, pageWidth - marginX, marginY + 3);

    // Metadata Section
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, marginX, marginY + 12);
    doc.text(`Tenant: ${tenant?.name || "N/A"}`, marginX, marginY + 19);

    let currentY = marginY + 30;

    // Iterate news items
    filteredNews.forEach((item) => {
      // Page break check
      if (currentY > pageHeight - 40) {
        doc.addPage();
        currentY = marginY;
      }

      // Published Date
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(100);
      const formattedDate = formatDate(item.published_at);
      doc.text(formattedDate, marginX, currentY);
      currentY += 6;

      // Categories
      const categoriesText = item.event_categories
        .map((cat) => cat.charAt(0).toUpperCase() + cat.slice(1).replace(/_/g, " "))
        .join(", ");
      doc.setFontSize(8);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(150);
      doc.text(categoriesText, marginX, currentY);
      currentY += 7;

      // Title
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(40);
      const titleLines = doc.splitTextToSize(item.title, contentWidth);
      doc.text(titleLines, marginX, currentY);
      currentY += titleLines.length * 7;

      // Snippet
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(75);
      const snippetLines = doc.splitTextToSize(item.snippet, contentWidth);
      doc.text(snippetLines, marginX, currentY);
      currentY += snippetLines.length * 7;

      // Source link
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 102, 204); // A nice blue for links
      const sourceText = `Source: ${item.domain}`;
      doc.text(sourceText, marginX, currentY);
      doc.link(
        marginX,
        currentY - 4,
        doc.getTextWidth(sourceText),
        6,
        { url: item.url }
      );

      currentY += 12;
    });

    // Watermark
    if (tenant?.logoUrl) {
      try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = tenant.logoUrl as string;
        });

        doc.saveGraphicsState();
        doc.setGState(new (doc.GState as any)({ opacity: 0.07 }));

        const watermarkWidth = 90;
        const watermarkHeight = (img.height / img.width) * watermarkWidth;
        const xPos = (pageWidth - watermarkWidth) / 2;
        const yPos = (pageHeight - watermarkHeight) / 2;

        doc.addImage(img, "PNG", xPos, yPos, watermarkWidth, watermarkHeight);
        doc.restoreGraphicsState();
      } catch (error) {
        console.warn("Failed to load watermark:", error);
      }
    }

    // Footer on all pages
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(130);
      doc.text(`Page ${i} of ${totalPages} | Confidential`, pageWidth / 2, pageHeight - 10, { align: "center" });
    }

    // Save PDF
    doc.save(`news-feed-${tenantId}-${new Date().getTime()}.pdf`);
  } catch (error) {
    console.error("Error generating PDF:", error);
  } finally {
    setDownloadingPdf(false);
  }
};


 const downloadWord = async () => {
  setDownloadingWord(true);
  try {
    const {
      Document,
      Paragraph,
      TextRun,
      HeadingLevel,
      AlignmentType,
      ImageRun,
      Packer,
      convertInchesToTwip,
      PageOrientation,
      Footer,
      Table,
      TableRow,
      TableCell,
      WidthType,
    } = await import("docx");

    // Load logo
    let logoImageData: {
      data: Uint8Array;
      mimeType: "jpg" | "png" | "gif" | "bmp";
    } | null = null;
    
    if (tenant?.logoUrl) {
      try {
        const response = await fetch(tenant.logoUrl);
        const blob = await response.blob();
        
        const getMimeType = (blobType: string): "jpg" | "png" | "gif" | "bmp" => {
          if (blobType.includes("png")) return "png";
          if (blobType.includes("jpeg") || blobType.includes("jpg")) return "jpg";
          if (blobType.includes("gif")) return "gif";
          if (blobType.includes("bmp")) return "bmp";
          return "png";
        };

        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result as string;
            const base64Data = result.split(",")[1];
            resolve(base64Data);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        logoImageData = { data: bytes, mimeType: getMimeType(blob.type) };
      } catch (error) {
        console.warn("Failed to load logo:", error);
      }
    }

    // Construct news items as table for better alignment
    const newsTableRows = filteredNews.map((item) => {
      return new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: formatDate(item.published_at),
                    bold: true,
                    color: "666666",
                    size: 10,
                  }),
                  new TextRun({
                    text: " | ",
                    color: "999999",
                    size: 10,
                  }),
                  new TextRun({
                    text: item.event_categories
                      .map(
                        (cat) =>
                          cat.charAt(0).toUpperCase() +
                          cat.slice(1).replace(/_/g, " ")
                      )
                      .join(", "),
                    italics: true,
                    color: "999999",
                    size: 10,
                  }),
                ],
                spacing: { after: 60 },
              }),
            ],
            width: { size: 25, type: WidthType.PERCENTAGE },
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: item.title,
                    bold: true,
                    color: "333333",
                    size: 11,
                  }),
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: item.snippet,
                    color: "666666",
                    size: 10,
                  }),
                ],
                spacing: { after: 150 },
              }),
            ],
            width: { size: 60, type: WidthType.PERCENTAGE },
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: item.domain,
                    color: "0066CC",
                    size: 9,
                   
                  }),
                ],
                spacing: { after: 150 },
              }),
            ],
            width: { size: 15, type: WidthType.PERCENTAGE },
           
          }),
        ],
      });
    });

    const doc = new Document({
      creator: "Your Application",
      title: "News Feed",
      description: "News feed document",
      styles: {
        paragraphStyles: [
          {
            id: "centerAlign",
            name: "Center Alignment",
            paragraph: {
              alignment: AlignmentType.CENTER,
              spacing: {
                before: 120,
                after: 120,
              },
            },
          },
          {
            id: "sectionHeader",
            name: "Section Header",
            paragraph: {
              spacing: {
                before: 240,
                after: 120,
              },
            },
            run: {
              size: 12,
              bold: true,
              color: "555555",
            },
          },
        ],
      },
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: convertInchesToTwip(1),
                right: convertInchesToTwip(0.75),
                bottom: convertInchesToTwip(1),
                left: convertInchesToTwip(0.75),
              },
              size: {
                orientation: PageOrientation.PORTRAIT,
              },
            },
          },
          children: [
            new Paragraph({
              text: "News Feed",
              heading: HeadingLevel.HEADING_1,
              alignment: AlignmentType.CENTER,
            }),
            new Paragraph({
              text: `Generated on: ${new Date().toLocaleDateString()}`,
              alignment: AlignmentType.CENTER,
            }),
            new Paragraph({
              text: `Tenant: ${tenant?.name || "N/A"}`,
              alignment: AlignmentType.CENTER,
            }),
            
            // Add spacing before table
            new Paragraph({
              text: "",
              spacing: { after: 200 },
            }),
            
            // News Table
            new Table({
              rows: newsTableRows,
              width: {
                size: 100,
                type: WidthType.PERCENTAGE,
              },
              columnWidths: [1500, 3600, 900],
            }),
          ],
          footers: {
            default: new Footer({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `Confidential - Generated by ${tenant?.name || ""}`,
                      size: 10,
                      color: "888888",
                    }),
                  ],
                  alignment: AlignmentType.CENTER,
                }),
                ...(logoImageData
                  ? [
                      new Paragraph({
                        children: [
                          new ImageRun({
                            data: logoImageData.data,
                            type: logoImageData.mimeType,
                            transformation: { width: 60, height: 60 },
                          }),
                        ],
                        alignment: AlignmentType.RIGHT,
                        spacing: { before: 100 },
                      }),
                    ]
                  : []),
                // Additional footer space
                new Paragraph({
                  text: "",
                  spacing: { after: 100 },
                }),
              ],
            }),
          },
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const fileName = `news-feed-${tenantId}-${new Date().getTime()}.docx`;
    saveAs(blob, fileName);
  } catch (error) {
    console.error("Error generating Word document:", error);
  } finally {
    setDownloadingWord(false);
  }
};


  if (loading) {
    return (
      <div className="rounded-3xl bg-white bg-opacity-60 backdrop-blur-md p-6 max-w-4xl mt-0 2xl:mt-20  mx-auto shadow-md animate-pulse  ">
        {/* Skeleton Tabs */}
        <div className="flex gap-2 flex-wrap mb-4 w-full overflow-x-auto pb-2 px-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-6 w-20 bg-gray-200 rounded-full" />
          ))}
        </div>

        {/* Skeleton News Cards */}
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="flex flex-col gap-2 p-4 bg-gray-100 rounded-xl"
            >
              <div className="h-3 w-1/3 bg-gray-300 rounded" />
              <div className="h-4 w-2/3 bg-gray-300 rounded" />
              <div className="h-3 w-1/2 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data)
    return <p className="text-sm text-red-500">Failed to load data.</p>;

  // Process categories from the data
  const allCategories = data.flatMap((item) => item.event_categories);
  const uniqueCategories = Array.from(new Set(allCategories));

  const computedCategories = [
    { name: "All News", count: data.length },
    ...uniqueCategories.map((category) => ({
      name:
        category.charAt(0).toUpperCase() + category.slice(1).replace(/_/g, " "),
      count: data.filter((item) => item.event_categories.includes(category))
        .length,
    })),
  ];

  // Filter news based on selected category
  const filteredNews =
    selectedCategory === "All News"
      ? data
      : data.filter((item) =>
          item.event_categories.some(
            (cat) =>
              cat.charAt(0).toUpperCase() + cat.slice(1).replace(/_/g, " ") ===
              selectedCategory
          )
        );

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url); // open iframe on right side
  };

  return (
    <>
      <div className="rounded-3xl bg-[#FDFDFD] bg-opacity-60 backdrop-blur-md p-6 max-w-4xl mt-0 2xl:mt-20 mx-auto  shadow-md mb-30">
        {/* Tabs */}
        <div className="flex gap-2 mb-2 justify-end">
          <button
            onClick={downloadPDF}
            disabled={downloadingPdf}
            className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {downloadingPdf ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Generating...
              </>
            ) : (
              <>
                <RiDownloadLine className="w-4 h-4" /> PDF
              </>
            )}
          </button>

          <button
            onClick={downloadWord}
            disabled={downloadingWord}
            className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {downloadingWord ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Generating...
              </>
            ) : (
              <>
                <RiDownloadLine className="w-4 h-4" />
                Word
              </>
            )}
          </button>
        </div>
        <div className="flex gap-2 flex-wrap mb-4 w-full overflow-x-auto pb-2 px-2 rounded-xl backdrop-blur-md bg-white/30">
          {computedCategories.map((cat) => (
            <button
              key={cat.name}
              onClick={() => setSelectedCategory(cat.name)}
              className={`px-4 py-1.5 rounded-full text-xs  whitespace-nowrap  shadow-sm transition-all duration-200 cursor-pointer ${
                selectedCategory === cat.name
                  ? "bg-gray-50 text-blue-600 border border-gray-200"
                  : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
              }`}
            >
              {cat.name} ({cat.count})
            </button>
          ))}
        </div>

        {/* News List */}
        <div className=" pr-2 relative">
          {filteredNews.map((item, idx) => (
            <div key={idx} className="py-4 flex gap-4">
              <p className="text-sm text-gray-400 mb-1 min-w-[150px] text-start">
                {formatDate(item.published_at)}
              </p>
              <div>
                <div className="items-center gap-2 text-sm mb-1">
                  <span className="text-gray-400">
                    {item.event_categories
                      .map(
                        (cat) =>
                          cat.charAt(0).toUpperCase() +
                          cat.slice(1).replace(/_/g, " ")
                      )
                      .join(", ")}
                  </span>
                  <span className="text-gray-400">·</span>
                  <button
                    onClick={(e) => handleSourceClick(e, item.url)}
                    className="ml-2 border border-gray-200 text-gray-700 rounded-full px-2 py-0.5 text-xs cursor-pointer hover:bg-gray-50"
                  >
                    {item.domain}
                  </button>
                </div>
                <h3 className="text-sm font-medium text-gray-800 mb-1">
                  <div className="hover:text-black transition-colors">
                    {item.title}
                  </div>
                </h3>
                <p className="text-sm text-gray-600 leading-snug">
                  {item.snippet}
                </p>
              </div>
            </div>
          ))}
          <div className="pointer-events-none absolute right-0 top-0 h-full w-6 bg-gradient-to-l from-white to-transparent"></div>
        </div>
      </div>
      {iframeUrl && (
        <IframeViewer url={iframeUrl} onClose={() => setIframeUrl(null)} />
      )}
    </>
  );
};
